<script setup lang="ts">
  import { VPlusOutlined } from "@krainovsd/vue-icons";
  import { VButton, VInput, VPopover, VText } from "@krainovsd/vue-ui";
  import { computed, onMounted, ref } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRouter } from "vue-router";
  import { CACHE_NAMESPACE, CACHE_TTL, useCachedResource } from "@/entities/cache";
  import { useFavoritesStore } from "@/entities/favorites";
  import { useReportStore } from "@/entities/report";
  import { fetchReports } from "@/entities/statistics";
  import type { IReportListItem } from "@/entities/statistics";
  import Card from "@/components/atoms/Card.vue";
  import Ellipsis from "@/components/icons/Ellipsis.vue";
  import Search from "@/components/icons/Search.vue";
  import Star from "@/components/icons/Star.vue";
  import PickerButton from "@/components/molecules/PickerButton.vue";

  // import MorningReport from "./components/MorningReport.vue";

  const { t } = useI18n();
  const router = useRouter();
  const favoritesStore = useFavoritesStore();
  const reportStore = useReportStore();

  onMounted(() => void favoritesStore.load());

  // A unified list row, whether it comes from the API list or the favorites
  // store (the latter works offline).
  type ReportListEntry = { id: number; name: string; favorite: boolean };

  type ReportFilter = "all" | "favorite";
  const reportFilter = ref<ReportFilter>("all");
  const filterPopoverOpen = ref(false);

  // Translated options for the report-list filter popover.
  const filterOptions = computed<{ key: ReportFilter; label: string }[]>(() => [
    {
      key: "all",
      label: t("page.statistics.filter.all", {}, { default: "Все" }),
    },
    {
      key: "favorite",
      label: t("page.statistics.filter.favorite", {}, { default: "Избранные" }),
    },
  ]);

  const selectedFilterLabel = computed(
    () => filterOptions.value.find((option) => option.key === reportFilter.value)?.label ?? null,
  );

  function selectFilter(key: ReportFilter) {
    reportFilter.value = key;
    filterPopoverOpen.value = false;
  }

  const { data: reportsData, isLoading: isReportDataLoading } = useCachedResource<
    IReportListItem[]
  >({
    namespace: CACHE_NAMESPACE.StatisticsList,
    key: "all",
    ttl: CACHE_TTL.Short,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchReports(),
  });

  const reports = computed<IReportListItem[]>(() => reportsData.value ?? []);

  const searchQuery = ref("");

  // Memoised lookup of favourited ids — derived from `favoritesStore.items` so
  // the per-row check below stays reactive when the favourites are mutated
  // (Pinia setup-store methods like `isFavorite(...)` don't always re-track
  // their internal ref through the store proxy when called from an outer
  // computed, which is why stars wouldn't appear in the "all" view).
  const favoriteIdSet = computed(() => new Set(favoritesStore.items.map((item) => item.id)));

  // "all" merges the API list with favorites (by id) to flag each row; "favorite"
  // reads straight from the on-device favorites, so it works without a network.
  const baseEntries = computed<ReportListEntry[]>(() => {
    if (reportFilter.value === "favorite") {
      return favoritesStore.items.map((item) => ({ id: item.id, name: item.name, favorite: true }));
    }
    return reports.value.map((report) => ({
      id: report.id,
      name: report.name,
      favorite: favoriteIdSet.value.has(report.id),
    }));
  });

  const filteredReports = computed<ReportListEntry[]>(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return baseEntries.value;
    return baseEntries.value.filter((report) => report.name.toLowerCase().includes(query));
  });

  // The favorites view is driven by the store (offline), the "all" view by the
  // API request.
  const isListLoading = computed(() =>
    reportFilter.value === "favorite" ? !favoritesStore.loaded : isReportDataLoading.value,
  );

  function handleReportClick(id: number) {
    void router.push(`/report/${id}`);
  }

  function handleCreateReport() {
    void router.push("/create-report");
  }

  // Per-row menu open state. At most one popover is open at a time, tracked by
  // the report id of the row whose ellipsis was clicked.
  const openMenuId = ref<number | null>(null);

  function setMenuOpen(id: number, open: boolean) {
    openMenuId.value = open ? id : null;
  }

  // Id of the report whose favorite snapshot is currently being downloaded, so
  // the row can show a pending state and ignore repeat clicks.
  const favoritingId = ref<number | null>(null);

  // Add/remove a report to favorites from the list. Adding downloads the
  // report's saved filter and current table data and stores the full snapshot
  // on-device, so the report opens offline (cache-first) afterwards.
  async function toggleFavoriteFor(report: ReportListEntry) {
    if (favoritesStore.isFavorite(report.id)) {
      openMenuId.value = null;
      await favoritesStore.remove(report.id);
      return;
    }
    if (favoritingId.value !== null) return;
    // Keep the menu open while downloading so the row shows the pending label.
    favoritingId.value = report.id;
    try {
      const { savedFilter, reportData, previousReportData } = await reportStore.getReportSnapshot(
        report.id,
      );
      await favoritesStore.add({
        id: report.id,
        name: report.name,
        savedFilter,
        reportData,
        previousReportData,
      });
    } finally {
      favoritingId.value = null;
      openMenuId.value = null;
    }
  }
</script>

<template>
  <div :class="$style.base">
    <h1>{{ $t("page.statistics.title", {}, { default: "Статистика" }) }}</h1>
    <!-- <CardButton v-for="item in items" :key="item.route" label="item" /> -->
    <VButton size="large" :class="$style.newReportButton" @click="handleCreateReport()">
      <template #icon>
        <VPlusOutlined />
      </template>
      {{ $t("page.statistics.newReportButton", {}, { default: "Новый отчет" }) }}
    </VButton>
    <!-- <SplitButton :size="'large'" :items="BUTTON_ITEMS" @click="handleCreateReport" /> -->
    <!-- <MorningReport /> -->
    <section :class="$style.reports">
      <h2 :class="$style.reportsTitle">
        {{ $t("page.statistics.ourReports", {}, { default: "Ваши отчеты" }) }}
      </h2>
      <div :class="$style.reportsFilters">
        <PickerButton
          v-model:open="filterPopoverOpen"
          :value="selectedFilterLabel"
          :placeholder="$t('page.statistics.filter.title', {}, { default: 'Фильтр' })"
          mode="popover"
        >
          <ul :class="$style.filterOptions">
            <li
              v-for="option in filterOptions"
              :key="option.key"
              :class="[
                $style.filterOption,
                { [$style.filterOptionActive]: option.key === reportFilter },
              ]"
              @click="selectFilter(option.key)"
            >
              {{ option.label }}
            </li>
          </ul>
        </PickerButton>
        <VInput
          v-model="searchQuery"
          shape="round"
          size="large"
          allow-clear
          :class-name-root="$style.reportsFiltersInput"
          placeholder="Поиск"
        >
          <template #prefix>
            <Search />
          </template>
        </VInput>
      </div>
      <div v-if="filteredReports.length" :class="$style.reportItems">
        <Card
          v-for="report in filteredReports"
          :key="report.id"
          :class="$style.reportItem"
          @click="handleReportClick(report.id)"
        >
          <div :class="$style.reportItemContent">
            <span :class="$style.reportItemTitle">{{ report.name }}</span>
          </div>
          <!-- Stop click here so the menu trigger / menu items don't open the
               report via the Card's @click. -->
          <div :class="$style.reportItemMeta" @click.stop>
            <Star v-if="report.favorite" :class="$style.reportItemStar" :size="18" filled />
            <VPopover
              :model-value="openMenuId === report.id"
              :class-name-positioner-content="$style.menu"
              @update:model-value="(open) => setMenuOpen(report.id, open ?? false)"
            >
              <VButton :class="$style.menuTrigger">
                <Ellipsis />
              </VButton>
              <template #content>
                <button
                  type="button"
                  :class="$style.menuItem"
                  :disabled="favoritingId !== null"
                  @click="toggleFavoriteFor(report)"
                >
                  {{
                    report.favorite
                      ? $t("page.statistics.menu.unfavorite", {}, { default: "Из избранного" })
                      : favoritingId === report.id
                        ? $t("page.statistics.menu.favoriting", {}, { default: "Сохранение…" })
                        : $t("page.statistics.menu.favorite", {}, { default: "В избранное" })
                  }}
                </button>
              </template>
            </VPopover>
          </div>
        </Card>
      </div>
      <div v-else-if="searchQuery.trim() && baseEntries.length" :class="$style.reportsEmpty">
        <VText :class="$style.reportsEmptyText">
          {{
            $t(
              "page.statistics.reportsNotFoundText",
              {},
              { default: "По вашему запросу ничего не найдено." },
            )
          }}
        </VText>
      </div>
      <div
        v-else-if="reportFilter === 'favorite' && !baseEntries.length && favoritesStore.loaded"
        :class="$style.reportsEmpty"
      >
        <VText :class="$style.reportsEmptyText">
          {{
            $t(
              "page.statistics.favoritesEmptyText",
              {},
              { default: "Здесь будут избранные отчёты. Добавьте отчёт в избранное из его меню." },
            )
          }}
        </VText>
      </div>
      <div v-else-if="!baseEntries.length && !isListLoading" :class="$style.reportsEmpty">
        <VText :class="$style.reportsEmptyText">
          {{
            $t(
              "page.statistics.reportsEmptyText",
              {},
              { default: "Здесь будут ваши отчёты. Постройте первый через «Новый отчёт»." },
            )
          }}
        </VText>
      </div>
    </section>
  </div>
</template>

<style lang="scss" module>
  .base {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 16px 20px;

    overflow: auto;
    // overscroll-behavior: contain prevents the page from rubber-banding the
    // body underneath when the scrollable area reaches its end.
    overscroll-behavior: contain;

    display: flex;
    gap: 40px;
    flex-direction: column;

    // WebKit (and older Chromium) don't include `padding-bottom` of a flex /
    // overflow container in the scrollable area, so a padding-bottom that
    // clears the fixed BottomNavigation visually "exists" but you can't
    // actually scroll to reveal anything inside it — the last card still ends
    // up under the nav. A flex-item spacer via `::after` is included in
    // scrollHeight on every engine, so the user can scroll the full distance.
    &::after {
      content: "";
      flex-shrink: 0;
      // Nav height (57px) + breathing room + iOS home-indicator safe area.
      height: calc(57px + 32px + env(safe-area-inset-bottom, 0px));
    }
  }

  .newReportButton {
    padding: 0.5rem 1rem;

    display: flex;
    align-items: center;
    gap: 0.5rem;

    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;

    background-color: var(--brand);
    color: var(--brand-foreground);

    &:hover {
      color: var(--brand-foreground) !important;
      background-color: var(--brand) !important;
    }
  }

  .reports {
    width: 100%;

    // Grow to fill the viewport on the empty state, but never shrink below the
    // content's own height — otherwise the list overflows this box, the
    // `::after` spacer in `.base` gets positioned after the allocated box (not
    // after the real last item), and the bottom card ends up behind the navbar.
    flex: 1 0 auto;

    display: flex;
    gap: 0.75rem;
    flex-direction: column;
  }

  .reportsTitle {
    font-size: 1.25rem; // xl
    line-height: 1.75rem; // xl
    font-weight: 600;

    color: var(--foreground);
  }

  .reportsFilters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .reportsFiltersInput {
    width: 128px;
    border-radius: 9999px !important; //full
  }

  .reportItems {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .reportItem {
    padding: 0.75rem 0.75rem;

    display: flex;
    justify-content: space-between;
  }

  .reportItemContent {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
  }

  .reportItemTitle {
    font-size: 1rem; // base
    line-height: 1.5rem;
    color: var(--foreground);
  }

  .reportItemLabel {
    font-size: 0.875rem; // sm
    line-height: 1.25rem;
    color: var(--muted-foreground);
  }

  .reportsEmpty {
    padding: 1.5rem;
    border: 1px dashed var(--border);
  }

  .reportsEmptyText {
    color: var(--muted-foreground);
    font-size: 0.875rem; // sm
    line-height: 1.25rem; // sm
  }

  .icon {
    display: flex;
    align-items: flex-start;
  }

  .reportItemMeta {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .reportItemStar {
    color: var(--warning);
  }

  // Strip the default button chrome so the trigger looks like the bare ellipsis
  // icon it used to be, but is still a real button (focusable, hit-area, etc.).
  .menuTrigger {
    padding: 4px;

    background: transparent;
    border: none;
    color: var(--muted-foreground);

    &:hover {
      color: var(--foreground);
    }
  }

  .menu {
    min-width: 200px;
    padding: 4px;

    display: flex;
    flex-direction: column;
    gap: 0;

    background-color: var(--popover);
  }

  .menuItem {
    padding: 8px 12px;

    background: transparent;
    border: none;
    color: var(--foreground);
    cursor: pointer;
    text-align: left;

    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.25rem;

    &:hover {
      background-color: var(--accent);
    }
  }

  .filterOptions {
    list-style: none;
    margin: 0;
    padding: 0;

    display: flex;
    flex-direction: column;
  }

  .filterOption {
    padding: 8px 12px;

    color: var(--foreground);
    cursor: pointer;
    border-radius: var(--radius, 6px);

    font-size: 0.875rem;
    line-height: 1.25rem;

    &:hover {
      background-color: var(--accent);
    }
  }

  .filterOptionActive {
    font-weight: 600;
    color: var(--brand);
  }
</style>
